/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Purpose } from '../managers/sso';
import { extractErrorMessage } from '../utils/errors';
import { normalizeDialingCode } from '../utils/phoneUtils';
import { useChangeUserName, useUser } from './account';
import { useCultureCode } from './useCultureCode';
import { useMutationErrorState } from './useMutationErrorState';
import {
  useRequestEmailOtp,
  useRequestPhoneNumberOtp,
  useVerifyEmailOtp,
  useVerifyPhoneNumberOtp,
} from './otp/api';

import { describeError } from '../utils/describeError';
export interface UseEmailUpdateFlowOptions {
  onSuccess?: () => void | Promise<void>;
  onError?: (error: string) => void;
  emailOtpPurpose?: Purpose;
}

export function useEmailUpdateFlow(options?: UseEmailUpdateFlowOptions) {
  const { t } = useTranslation('account');
  const { data: user } = useUser('useEmailUpdateFlow');
  const cultureCode = useCultureCode('useEmailUpdateFlow');

  const optionsRef = useRef(options);
  optionsRef.current = options;

  const requestPhoneNumberOtpMutation =
    useRequestPhoneNumberOtp('useEmailUpdateFlow');
  const verifyPhoneNumberOtpMutation =
    useVerifyPhoneNumberOtp('useEmailUpdateFlow');
  const changeUserNameMutation = useChangeUserName('useEmailUpdateFlow');
  const requestEmailOtpMutation = useRequestEmailOtp('useEmailUpdateFlow');
  const verifyEmailOtpMutation = useVerifyEmailOtp('useEmailUpdateFlow');

  const { errorMessage, clearError, fail, run, runAsync } =
    useMutationErrorState(options?.onError);

  const requestPhoneOtp = useCallback(
    (onSuccess?: () => void, onError?: () => void) => {
      if (!user?.phoneNumber || !user?.dialingCode) {
        const error = t('verifyPhonePage.missingPhoneNumber');
        console.error(describeError(error));
        fail(error);
        onError?.();
        return;
      }

      run(
        requestPhoneNumberOtpMutation,
        {
          requestOtpBody: {
            dialingCode: normalizeDialingCode(user.dialingCode),
            phoneNumber: user.phoneNumber,
            ...(user.cultureCode && { cultureCode: user.cultureCode }),
          },
          otpPurpose: Purpose.USERNAME_CHANGE,
        },
        {
          fallbackMessage: t('verifyPhonePage.requestFailed'),
          onSuccess: () => {
            onSuccess?.();
            optionsRef.current?.onSuccess?.();
          },
          onError: () => onError?.(),
        },
      );
    },
    [user, requestPhoneNumberOtpMutation, t, fail, run],
  );

  const verifyPhoneOtp = useCallback(
    (otp: string, onSuccess?: () => void) => {
      if (!user?.username) {
        const error = t('verifyEmailPage.missingEmail');
        console.error(describeError(error));
        fail(error);
        return;
      }

      run(
        verifyPhoneNumberOtpMutation,
        {
          verifyOtpBody: {
            email: user.username,
            otp: otp,
            purpose: Purpose.USERNAME_CHANGE,
          },
          otpPurpose: Purpose.USERNAME_CHANGE,
        },
        {
          fallbackMessage: t('verifyPhonePage.verificationFailed'),
          onSuccess: () => onSuccess?.(),
        },
      );
    },
    [user, verifyPhoneNumberOtpMutation, t, fail, run],
  );

  const changeEmail = useCallback(
    (newEmail: string, onSuccess?: () => void) => {
      if (!user?.cultureCode) {
        const error = t('updateEmailPage.missingCultureCode');
        console.error(describeError(error));
        fail(error);
        return;
      }

      clearError();
      changeUserNameMutation.mutate(
        { newUsername: newEmail, emailLanguageCode: cultureCode },
        {
          onSuccess: () =>
            run(
              requestEmailOtpMutation,
              {
                email: newEmail,
                cultureCode: cultureCode,
                purpose: Purpose.USERNAME_CHANGE,
              },
              {
                fallbackMessage: t('updateEmailPage.otpRequestFailed'),
                onSuccess: () => onSuccess?.(),
              },
            ),
          onError: (error: unknown) => {
            const apiMessage = extractErrorMessage(error, '');
            fail(
              apiMessage.toLowerCase().includes('already exists')
                ? t('updateEmailPage.emailAlreadyInUse')
                : apiMessage || t('updateEmailPage.changeFailed'),
            );
          },
        },
      );
    },
    [
      user,
      changeUserNameMutation,
      requestEmailOtpMutation,
      cultureCode,
      t,
      fail,
      run,
      clearError,
    ],
  );

  const requestEmailOtp = useCallback(
    (email: string, onSuccess?: () => void, onError?: () => void) => {
      if (!user?.cultureCode) {
        const error = t('updateEmailPage.missingCultureCode');
        console.error(describeError(error));
        fail(error);
        onError?.();
        return;
      }

      run(
        requestEmailOtpMutation,
        {
          email: email,
          cultureCode: cultureCode,
          purpose:
            optionsRef.current?.emailOtpPurpose ?? Purpose.USERNAME_CHANGE,
        },
        {
          fallbackMessage: t('updateEmailPage.otpRequestFailed'),
          onSuccess: () => onSuccess?.(),
          onError: () => onError?.(),
        },
      );
    },
    [user, requestEmailOtpMutation, cultureCode, t, fail, run],
  );

  const verifyEmailOtp = useCallback(
    async (email: string, otp: string) => {
      await runAsync(
        verifyEmailOtpMutation,
        {
          email: email,
          otp: otp,
          purpose:
            optionsRef.current?.emailOtpPurpose ?? Purpose.USERNAME_CHANGE,
        },
        {
          fallbackMessage: t('verifyEmailPage.verificationFailed'),
          onSuccess: () => optionsRef.current?.onSuccess?.(),
        },
      );
    },
    [verifyEmailOtpMutation, t, runAsync],
  );

  const isLoading =
    requestPhoneNumberOtpMutation.isPending ||
    verifyPhoneNumberOtpMutation.isPending ||
    changeUserNameMutation.isPending ||
    requestEmailOtpMutation.isPending ||
    verifyEmailOtpMutation.isPending;

  return useMemo(
    () => ({
      errorMessage,
      isLoading,

      requestPhoneOtp,
      verifyPhoneOtp,
      changeEmail,
      requestEmailOtp,
      verifyEmailOtp,
      clearError,

      isRequestingPhoneOtp: requestPhoneNumberOtpMutation.isPending,
      isVerifyingPhoneOtp: verifyPhoneNumberOtpMutation.isPending,
      isChangingEmail: changeUserNameMutation.isPending,
      isRequestingEmailOtp: requestEmailOtpMutation.isPending,
      isVerifyingEmailOtp: verifyEmailOtpMutation.isPending,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      errorMessage,
      isLoading,
      requestPhoneNumberOtpMutation.isPending,
      verifyPhoneNumberOtpMutation.isPending,
      changeUserNameMutation.isPending,
      requestEmailOtpMutation.isPending,
      verifyEmailOtpMutation.isPending,
    ],
  );
}
